import React from "react";
import { ArrowRight, BriefcaseBusiness, CalendarCheck2, ChevronRight, ClipboardCheck } from "lucide-react";
import { ActionRequiredCount } from "../../../types";

type ActionRequiredTileProps = {
    count: ActionRequiredCount;
    isLoading?: boolean;
    onNavigate: () => void;
};

type ActionRequiredSummaryRow = {
    label: string;
    count: number;
    icon: React.ElementType;
    iconClassName: string;
};

const getCount = (byCategory: Record<string, number>, categoryCode: string): number => {
    const value = byCategory[categoryCode];
    return Number.isFinite(Number(value)) ? Number(value) : 0;
};

const formatItemCount = (total: number): string => {
    return `${total} ${total === 1 ? "item needs" : "items need"} your attention`;
};

const ActionRequiredTile: React.FC<ActionRequiredTileProps> = ({ count, isLoading = false, onNavigate }) => {
    const byCategory = count.by_category || {};
    const total = Number.isFinite(Number(count.total)) ? Number(count.total) : 0;
    const rows: ActionRequiredSummaryRow[] = [
        {
            label: "Timesheet Approvals",
            count:
                getCount(byCategory, "EMPLOYEE_TIMECARD_REQUEST") +
                getCount(byCategory, "TIMECARD_MEAL_BREAK") +
                getCount(byCategory, "TIMECARD_APPROVAL"),
            icon: ClipboardCheck,
            iconClassName: "bg-blue-50 text-blue-600",
        },
        {
            label: "Time-Off Requests",
            count: getCount(byCategory, "TIMEOFF_APPROVAL") + getCount(byCategory, "EMPLOYEE_UNAVAILABLITY"),
            icon: CalendarCheck2,
            iconClassName: "bg-green-50 text-green-600",
        },
        {
            label: "Overtime Requests",
            count: getCount(byCategory, "EMPLOYEE_EXTRA_HOURS"),
            icon: BriefcaseBusiness,
            iconClassName: "bg-orange-50 text-orange-500",
        },
    ];

    return (
        <section className="flex h-full flex-col rounded-lg border border-gray-300 bg-white p-6 mb-4">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-orange-300 bg-orange-50 text-orange-500">
                        <ClipboardCheck className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold leading-tight text-gray-900">Action Required</h2>
                        <p className="text-xs leading-5 text-gray-500">
                            {isLoading ? "Checking your open actions..." : formatItemCount(total)}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onNavigate}
                    className="flex flex-shrink-0 items-center gap-2 rounded-full text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    aria-label="View all actions"
                >
                    <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-red-500 px-2 text-sm font-semibold leading-none text-white">
                        {total > 99 ? "99+" : total}
                    </span>
                    <ChevronRight className="h-5 w-5" />
                </button>
            </div>

            <div className="mt-4 space-y-2">
                {rows.map((row) => {
                    const Icon = row.icon;

                    return (
                        <button
                            type="button"
                            key={row.label}
                            onClick={onNavigate}
                            className="flex w-full items-center justify-between gap-3 rounded-md py-0.5 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            <span className="flex min-w-0 items-center gap-3">
                                <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md ${row.iconClassName}`}>
                                    <Icon className="h-3.5 w-3.5" />
                                </span>
                                <span className="truncate text-sm font-medium text-gray-700">{row.label}</span>
                            </span>
                            <span className="flex flex-shrink-0 items-center gap-3 text-sm font-semibold text-gray-800">
                                {isLoading ? (
                                    <span className="h-4 w-5 animate-pulse rounded bg-gray-200" aria-label="Loading" />
                                ) : (
                                    row.count
                                )}
                                <ChevronRight className="h-4 w-4 text-gray-400" />
                            </span>
                        </button>
                    );
                })}
            </div>

            <button
                type="button"
                onClick={onNavigate}
                className="mt-auto flex w-full items-center justify-center gap-2 rounded-md pt-4 text-sm font-semibold text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
                <span>View all actions</span>
                <ArrowRight className="h-4 w-4" />
            </button>
        </section>
    );
};

export default ActionRequiredTile;
